/* eslint-disable react/no-multi-comp */
/* eslint-disable react/display-name */
import React, { forwardRef, useState } from 'react';
import { NavLink as RouterLink, useLocation, matchPath } from 'react-router-dom';
import PropTypes from 'prop-types';
import { makeStyles } from 'tss-react/mui';
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  Hidden,
  lighten,
  Divider,
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import WhatsUtils from 'commons/utils/whatsUtils';
import { MINHAAGENDA_PHONE } from 'commons/constants';
import { currentUserSelector, resetStore, useAppStore } from 'stores/appStore';

const useStyles = makeStyles()((theme, _params, classes) => ({
  root: {},
  item: {
    display: 'flex',
    paddingTop: 0,
    paddingBottom: 0,
  },
  button: {
    // color: colors.blueGrey[800],
    // padding: '10px 8px',
    // justifyContent: 'flex-start',
    // letterSpacing: 0,
    // width: '100%',
    [`& .${classes.label}`]: {
      textTransform: 'none',
      fontWeight: theme.typography.fontWeightMedium,
      justifyContent: 'flex-start',
    },
  },
  icon: {
    color: theme.palette.icon,
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(1),
  },
  label: {},
  exitButton: {
    color: theme.palette.error.main,
  },

  active: {
    color: 'white',
    fontWeight: theme.typography.fontWeightMedium,
    [`& .${classes.icon}`]: {
      color: 'white',
    },
    [`& .${classes.label}`]: {
      color: 'white',
    },
    [`& .${classes.iconRoot}`]: {
      color: 'white',
    },
    [`& .${classes.iconSubRoot}`]: {
      color: 'white',
    },
    [`& .${classes.listItemTextRoot}`]: {
      color: 'white',
      fontWeight: theme.typography.fontWeightMedium,
    },
    boxShadow: 'rgba(58, 53, 65, 0.42) 0px 4px 8px -4px',
    backgroundImage: `linear-gradient(98deg, ${lighten('#8C21E5', 0.3)}, #8C21E5 94%)`,
  },
  nested: {
    paddingLeft: theme.spacing(6),
  },
  iconRoot: {
    minWidth: '40px',
    color: theme.palette.primary.main,
  },
  iconSubRoot: {
    minWidth: '25px',
  },
  listItemGutters: {
    paddingLeft: theme.spacing(1.8),
    paddingRight: '8px',
    minHeight: '42px',
  },
  listItem: {
    borderRadius: '0px 100px 100px 0px',
  },
  listItemTextRoot: {
    color: theme.palette.primary.main,
  },
  sectionTitle: {
    color: theme.palette.text.secondary,
    fontSize: '12px',
    marginTop: '2px',
    marginLeft: '5px',
    marginBottom: '-4px',
  },
}));

const CustomRouterLink = forwardRef((props, ref) => (
  <div ref={ref} style={{ flexGrow: 1 }}>
    <RouterLink {...props} />
  </div>
));

const SidebarNav = ({ pages, className, open, ...rest }) => {
  const currentUser = useAppStore(currentUserSelector);
  const location = useLocation();

  const handleLogout = () => {
    resetStore();
  };

  const [openMenus, setOpenMenus] = useState(() => {
    return pages
      .filter((p) => p.subMenus)
      .reduce((prev, current) => {
        return {
          ...prev,
          [current.title]: current.open ? true : false,
        };
      }, {});
  });

  const { classes, cx } = useStyles();

  const handleOpen = (page) => {
    setOpenMenus((openMenus1) => ({
      ...openMenus1,
      [page.title]: openMenus1[page.title] ? false : true,
    }));
  };

  const userRoles = currentUser != null ? currentUser.roles : [];
  const navActionsAllowed = pages
    .filter((nav) => !nav.requiredRoles || nav.requiredRoles.some((val) => userRoles.indexOf(val) !== -1))
    .map((navAction) => {
      if (navAction.subMenus != null) {
        return {
          ...navAction,
          subMenus: navAction.subMenus.filter(
            (sub) => !sub.requiredRoles || sub.requiredRoles.some((val) => userRoles.indexOf(val) !== -1),
          ),
        };
      }
      return navAction;
    })
    .filter((nav1) => !nav1.subMenus || nav1.subMenus.length > 0);

  return (
    <List {...rest} className={cx(classes.root, className)}>
      {navActionsAllowed.map((page) => {
        const active = page.href
          ? !!matchPath(
              {
                path: page.href,
                end: false,
              },
              location.pathname,
            )
          : false;

        return (
          <React.Fragment key={page.title}>
            {page.subMenus && (
              <ListItem
                button
                onClick={() => handleOpen(page)}
                classes={{ gutters: classes.listItemGutters }}
              >
                <ListItemIcon classes={{ root: classes.iconRoot }}>
                  <FontAwesomeIcon icon={page.icon} size="lg" fixedWidth />
                </ListItemIcon>
                {open && <ListItemText primary={page.title} classes={{ root: classes.listItemTextRoot }} />}
                {openMenus[page.title] ? <ExpandLess /> : <ExpandMore />}
              </ListItem>
            )}

            {!page.subMenus && (
              <>
                {page.sectionTitle && (
                  <Typography variant="subtitle2" className={classes.sectionTitle}>
                    {page.sectionTitle}
                  </Typography>
                )}
                <ListItem
                  button
                  component={CustomRouterLink}
                  to={page.href}
                  className={cx(active && classes.active)}
                  classes={{ gutters: classes.listItemGutters, root: classes.listItem }}
                >
                  <ListItemIcon classes={{ root: classes.iconRoot }}>
                    <FontAwesomeIcon icon={page.icon} size="lg" fixedWidth />
                  </ListItemIcon>
                  {open && (
                    <ListItemText primary={page.title} classes={{ primary: classes.listItemTextRoot }} />
                  )}
                </ListItem>
                {page.dividerAfter && <Divider />}
              </>
            )}

            {page.subMenus && page.subMenus.length > 0 && (
              <>
                <Collapse in={openMenus[page.title]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {page.subMenus.map((sub) => (
                      <ListItem
                        button
                        className={active ? cx(classes.nested, classes.active) : classes.nested}
                        key={sub.title}
                        component={CustomRouterLink}
                        activeClassName={classes.active}
                        to={sub.href}
                      >
                        <ListItemIcon classes={{ root: classes.iconSubRoot }}>
                          <FontAwesomeIcon icon={sub.icon} size="sm" fixedWidth />
                        </ListItemIcon>
                        <ListItemText primary={sub.title} classes={{ primary: classes.listItemTextRoot }} />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </>
            )}
          </React.Fragment>
        );
      })}

      {/* SUPPORT */}
      <ListItem
        button
        onClick={() => {
          const message = `Informações para agilizar o atendimento:\nNome: ${currentUser?.principal.name}\nEmail: ${currentUser?.principal.email}\nVersão do sistema: WEB`;

          WhatsUtils.sendMessage({
            phone: MINHAAGENDA_PHONE,
            text: message,
            shouldNotCleanUpNumber: true,
          });
        }}
        classes={{ gutters: classes.listItemGutters }}
      >
        <ListItemIcon classes={{ root: classes.iconRoot }}>
          <FontAwesomeIcon icon="circle-question" size="lg" fixedWidth />
        </ListItemIcon>
        {open && <ListItemText primary="Suporte" classes={{ primary: classes.listItemTextRoot }} />}
      </ListItem>

      {/* EXIT */}
      <Hidden smUp>
        <ListItem button onClick={() => handleLogout()} classes={{ gutters: classes.listItemGutters }}>
          <ListItemIcon classes={{ root: classes.iconRoot }}>
            <FontAwesomeIcon icon="right-from-bracket" size="lg" fixedWidth color="red" />
          </ListItemIcon>
          {open && (
            <ListItemText
              disableTypography
              primary={
                <Typography type="body1" style={{ color: 'red' }}>
                  Sair
                </Typography>
              }
            />
          )}
        </ListItem>
      </Hidden>
    </List>
  );
};

SidebarNav.propTypes = {
  className: PropTypes.string,
  pages: PropTypes.array.isRequired,
};

export default SidebarNav;
